import React, { useState, useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import CountryBlogDetail from "@/pages/country/CountryBlogDetail";
import BlogDetails from "@/components/homepage/BlogDetails";
import { getBlogs } from "@/services/common/blogs";
// import { Spin } from "antd";
import SEOHead from "@/components/SEO/SEOHead";
import { Helmet } from "react-helmet-async";

export default function BlogDetailRouter() {
  const { slug, id } = useParams();
  const blogId = Number(id);

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const all = await getBlogs();
        const found = all.find((b) => b.id === blogId && b.slug === slug);
        setBlog(found);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [blogId, slug]);

  // Generate SEO description from blog content
  const generateDescription = (blog) => {
    if (!blog) return "";
    if (!blog.contents || blog.contents.length === 0) return "";

    // Strip HTML from the first section content to create a clean description
    const content = blog.contents[0]?.sectionContent || "";
    const plainText = content.replace(/<[^>]+>/g, "");
    return plainText.slice(0, 160) + (plainText.length > 160 ? "..." : "");
  };

  // Extract keywords from blog
  const extractKeywords = (blog) => {
    if (!blog) return "";
    return blog.keywords ? blog.keywords.join(", ") : "";
  };

  // Generate structured data for the blog
  const generateStructuredData = (blog) => {
    if (!blog) return null;

    // Get the base URL from the environment or use a default
    const baseUrl = import.meta.env.VITE_URL || window.location.origin;
    const blogUrl = `${baseUrl}/blog/${slug}/${id}`;

    // Get the first image from the blog content, if available
    const blogImage =
      blog.coverImages && blog.coverImages.length > 0
        ? blog.coverImages[0]
        : null;

    // Create the structured data object
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": blogUrl,
      },
      headline: blog.title,
      description: generateDescription(blog),
      image: blogImage,
      author: {
        "@type": "Person",
        name: blog.authorName || "Inforens",
      },
      publisher: {
        "@type": "Organization",
        name: "Inforens",
        logo: {
          "@type": "ImageObject",
          url: `${baseUrl}/square-logo.jpeg`,
        },
      },
      datePublished: blog.createdAt,
      dateModified: blog.updatedAt,
      keywords: blog.keywords || [],
    };

    return structuredData;
  };

  if (loading)
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        {/* <Spin size="large" /> */}
      </div>
    );
  if (!blog) return <Navigate to="/?error=Not_Found" />;

  // Get the base URL from the environment or use a default
  const baseUrl = import.meta.env.VITE_URL || window.location.origin;
  const blogUrl = `${baseUrl}/blog/${slug}/${id}`;

  // Get the first image from the blog content, if available
  const blogImage =
    blog.coverImages && blog.coverImages.length > 0
      ? blog.coverImages[0]
      : null;

  const structuredData = generateStructuredData(blog);

  // if this blog has a countryCode, render the country-style layout:
  if (blog.metadata?.countryCode) {
    return (
      <>
        <SEOHead
          title={`${blog.title} | ${
            blog.metadata?.countryCode
              ? `Study in ${blog.metadata?.country || "Abroad"}`
              : "Blog"
          }`}
          description={generateDescription(blog)}
          keywords={extractKeywords(blog)}
          ogTitle={blog.title}
          ogDescription={generateDescription(blog)}
          ogImage={blogImage}
          ogUrl={blogUrl}
          ogType="article"
        />
        {structuredData && (
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify(structuredData)}
            </script>
          </Helmet>
        )}
        <CountryBlogDetail blog={blog} />
      </>
    );
  }

  // otherwise render the single-section "standard" blog:
  return (
    <>
      <SEOHead
        title={`${blog.title} | Blog`}
        description={generateDescription(blog)}
        keywords={extractKeywords(blog)}
        ogTitle={blog.title}
        ogDescription={generateDescription(blog)}
        ogImage={blogImage}
        ogUrl={blogUrl}
        ogType="article"
      />
      {structuredData && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        </Helmet>
      )}
      <BlogDetails blog={blog} />
    </>
  );
}
